import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, CardBody, Badge } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Spinner } from "reactstrap";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import CommonDataTable from "../../common/DataTable";
import { getUserColumns } from "../../Components/Common/columns/Users";
import { fetchUsers, bulkDeleteUsers, fetchUserHistory, fetchUserDetails, addNewUser } from "../../slices/users/thunk";
import { updateUser } from "../../helpers/apis";
import DeleteDialog from "../../Components/Common/DeleteDailog";
import CreateBookingModal from "../../Components/Bookings/CreateBookingModal";
import { showToast } from "../../slices/toast/reducer";

const Users = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { users, pagination, loading, userHistory, userDetails } = useSelector((state) => ({
    users: state.Users.users,
    pagination: state.Users.pagination,
    loading: state.Users.loading,
    userHistory: state.Users.userHistory,
    userDetails: state.Users.userDetails,
  }));

  const [filterParams, setFilterParams] = useState({
    page: 1,
    limit: 10,
    search: "",
    loginType: "",
  });

  const [selectedRows, setSelectedRows] = useState([]);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Delete states
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [deleteUserIds, setDeleteUserIds] = useState([]);
  const [deleting, setDeleting] = useState(false);

  // Booking modal state
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [selectedUserForBooking, setSelectedUserForBooking] = useState(null);
  // Add User Modal State
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", phone: "" });

  // Edit User Modal State
  const [editUserModalOpen, setEditUserModalOpen] = useState(false);
  const [editUserData, setEditUserData] = useState({
    id: "",
    name: "",
    email: "",
    mobileNo: "",
    loginType: "",
  });
  const [updatingUser, setUpdatingUser] = useState(false);

  const toggleAddUserModal = () => {
    setAddUserModalOpen(!addUserModalOpen);
    setNewUser({ name: "", email: "", phone: "" }); // Reset form on toggle
  };

  const handleOpenEditUser = (user) => {
    setEditUserData({
      id: user?.id || user?._id || user?.userId || "",
      name: user?.name || "",
      email: user?.email || "",
      mobileNo: user?.mobileNo || user?.mobile || "",
      loginType: user?.loginType || "",
    });
    setEditUserModalOpen(true);
  };

  const handleUpdateUser = async () => {
    if (!editUserData?.id) {
      dispatch(showToast({ type: "error", msg: "User id is missing" }));
      return;
    }

    if (!editUserData.name) {
      dispatch(showToast({ type: "warning", msg: "Name is required" }));
      return;
    }

    setUpdatingUser(true);
    try {
      const name = (editUserData.name ?? "").toString().trim();
      const email = (editUserData.email ?? "").toString().trim();
      const mobileNo = (editUserData.mobileNo ?? "").toString().trim();
      const loginType = (editUserData.loginType ?? "").toString().trim();

      const payload = {
        name,
        ...(email ? { email } : {}),
        ...(mobileNo ? { mobileNo } : {}),
        ...(loginType ? { loginType } : {}),
      };

      const response = await updateUser(editUserData.id, payload);

      if (response?.error) {
        throw new Error(response.msg || "Failed to update user");
      }

      dispatch(showToast({ type: "success", msg: "User updated successfully" }));
      setEditUserModalOpen(false);

      // Refresh list to reflect updates
      await dispatch(fetchUsers(filterParams));
    } catch (error) {
      console.error("Update user error:", error);
      dispatch(showToast({ type: "error", msg: error.message || "Failed to update user" }));
    } finally {
      setUpdatingUser(false);
    }
  };

  const handleAddUser = async () => {
    if (!newUser.name || (!newUser.email && !newUser.phone)) {
      // Show error toast or validation message
      return;
    }

    // Determine which field to send based on input
    const userData = {
      name: newUser.name
    };
    if (newUser.email) userData.email = newUser.email;
    if (newUser.phone) userData.mobileNo = newUser.phone;

    const result = await dispatch(addNewUser(userData));
    if (addNewUser.fulfilled.match(result)) {
      setAddUserModalOpen(false);
      dispatch(fetchUsers(filterParams)); // Refresh list
    }
  };

  // Fetch users when filterParams change
  useEffect(() => {
    const cleanedParams = {};
    Object.keys(filterParams).forEach((key) => {
      const value = filterParams[key];
      if (value !== "" && value !== undefined && value !== null && key !== "currentPage") {
        cleanedParams[key] = value;
      }
    });
    dispatch(fetchUsers(cleanedParams));
  }, [dispatch, filterParams]);

  const handleFetchData = useCallback(
    (newParams) => {
      setFilterParams((prevParams) => {
        const updatedParams = {
          ...prevParams,
          ...newParams,
          limit: newParams.perPage || newParams.limit || prevParams.limit,
          page: newParams.currentPage || newParams.page || prevParams.page,
        };
        delete updatedParams.perPage;
        delete updatedParams.currentPage;

        return updatedParams;
      });
    },
    []
  );

  const handleBulkDeleteClick = () => {
    if (selectedRows.length === 0) {
      // You might want to show a toast here
      console.log("No users selected for deletion");
      return;
    }

    const userIds = selectedRows.map((row) => row.id);
    setDeleteUserIds(userIds);
    setBulkDeleteModalOpen(true);
  };

  const handleBulkDeleteConfirm = async () => {
    if (deleteUserIds.length === 0) {
      setBulkDeleteModalOpen(false);
      return;
    }

    setDeleting(true);
    try {
      await dispatch(bulkDeleteUsers({ userIds: deleteUserIds }));
      setSelectedRows([]);
      setDeleteUserIds([]);
      // Refetch users after deletion
      await dispatch(fetchUsers(filterParams));
    } catch (error) {
      console.error("Error deleting users:", error);
    } finally {
      setDeleting(false);
      setBulkDeleteModalOpen(false);
    }
  };

  const handleRowClick = (user) => {
    setSelectedUser(user);
    // Fetch user details
    dispatch(fetchUserDetails(user.id));
    setDetailsModalOpen(true);
  };

  const handleCreateBookingClick = (user) => {
    setSelectedUserForBooking(user);
    setBookingModalOpen(true);
  };

  const handleHistoryClick = (user) => {
    setSelectedUser(user);
    setHistoryModalOpen(true);
    // Fetch user history
    dispatch(fetchUserHistory({ userId: user.id }));
  };

  const handleLoginTypeFilter = (e) => {
    const loginType = e.target.value;
    const updatedParams = {
      ...filterParams,
      loginType: loginType,
      page: 1, // Reset to first page when filtering
    };
    setFilterParams(updatedParams);
  };

  const handleDeleteClick = (user) => {
    setDeleteUserId(user.id);
    setDeleteModalOpen(true);
  };

  const handleSingleDeleteConfirm = async () => {
    if (!deleteUserId) {
      return;
    }

    setDeleting(true);
    try {
      await dispatch(bulkDeleteUsers({ userIds: [deleteUserId] }));
      setDeleteUserId(null);
      setDeleteModalOpen(false);
      setSelectedRows([]); // Clear selection after delete
      setDetailsModalOpen(false); // Close details modal if open
      // Refetch users after deletion
      await dispatch(fetchUsers(filterParams));
    } catch (error) {
      console.error("Error deleting user:", error);
    } finally {
      setDeleting(false);
    }
  };

  const columns = getUserColumns({
    onHistoryClick: handleHistoryClick,
    onDeleteClick: handleDeleteClick,
    onRowClick: handleRowClick,
    onCreateBookingClick: handleCreateBookingClick,
    onEditClick: handleOpenEditUser,
  });

  const handleBookingCreated = (bookingData) => {
    // Handle the booking creation
    console.log("Booking created:", bookingData);
    // You might want to refresh the users list or show a toast
  };

  // Function to get hours and category from booking type
  const getBookingDuration = (bookingType) => {
    if (!bookingType) return { category: null, hours: 12 };

    const isWeekend = bookingType.includes("WEEKEND");
    const is24Hr = bookingType.includes("24HR");

    return {
      category: isWeekend ? "Weekend" : "Regular",
      hours: is24Hr ? 24 : 12,
    };
  };

  // Function to get badge color based on booking type
  const getBookingTypeBadge = (bookingType) => {
    const duration = getBookingDuration(bookingType);

    let badgeColor = 'primary';
    if (duration.category === 'Weekend') {
      badgeColor = duration.hours === 24 ? 'warning' : 'info';
    } else {
      badgeColor = duration.hours === 24 ? 'success' : 'primary';
    }

    return badgeColor;
  };

  // Function to format booking type for display
  const formatBookingType = (bookingType) => {
    const duration = getBookingDuration(bookingType);
    return `${duration.category} ${duration.hours}H`;
  };

  // Custom filters component
  const customFilters = (
    <div className="d-flex align-items-center gap-2">
      <Input
        type="select"
        value={filterParams.loginType}
        onChange={handleLoginTypeFilter}
        style={{ width: "180px" }}
      >
        <option value="">Login Type</option>
        <option value="phone">Phone</option>
        <option value="google">Google</option>
        <option value="admin">Admin</option>
      </Input>
    </div>
  );

  document.title = "Users | " + process.env.REACT_APP_SITE_TITLE;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Users" pageTitle="Users" />
        </Container>
      </div>
      <div className="users-table-section">
        <CommonDataTable
          columns={columns}
          data={users}
          loading={loading}
          totalRows={pagination.totalUsers || 0}
          fetchData={handleFetchData}
          filterParams={filterParams}
          searchEnable={true}
          checkboxEnabled={true}
          bulkSelected={(selectedRows) => setSelectedRows(selectedRows)}
          bulkDelete={handleBulkDeleteClick}
          customFilters={customFilters}
          selectedRows={selectedRows}
          showExportButton={true}
          exportFileName="Users"
          showAddButton={true}
          buttonName="Add User"
          addOnClickMethod={toggleAddUserModal}
          exportColumns={columns}
          onRowClicked={handleRowClick}
        />
      </div>
      {/* Add User Modal */}
      <Modal isOpen={addUserModalOpen} toggle={toggleAddUserModal} centered>
        <ModalHeader toggle={toggleAddUserModal}>Add New User</ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <label className="form-label">Name</label>
            <Input
              type="text"
              placeholder="Enter full name"
              value={newUser.name}
              onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Email</label>
            <Input
              type="email"
              placeholder="Enter email"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
            />
          </div>
          <div className="text-center my-2">OR</div>
          <div className="mb-3">
            <label className="form-label">Phone Number</label>
            <Input
              type="text"
              placeholder="Enter phone number"
              value={newUser.phone}
              onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleAddUser}>
            Add User
          </Button>
        </ModalFooter>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        isOpen={editUserModalOpen}
        toggle={() => setEditUserModalOpen(false)}
        centered
      >
        <ModalHeader toggle={() => setEditUserModalOpen(false)}>
          <i className="ri-user-settings-line me-2" />
          Update User Details
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <label className="form-label fw-medium">Name *</label>
            <Input
              type="text"
              placeholder="Enter full name"
              value={editUserData.name}
              onChange={(e) => setEditUserData({ ...editUserData, name: e.target.value })}
            />
          </div>
          <div className="mb-3">
            <label className="form-label fw-medium">Email</label>
            <Input
              type="email"
              placeholder="Enter email"
              value={editUserData.email}
              onChange={(e) => setEditUserData({ ...editUserData, email: e.target.value })}
            />
          </div>
          <div className="mb-3">
            <label className="form-label fw-medium">Phone Number</label>
            <Input
              type="text"
              placeholder="Enter mobile number"
              value={editUserData.mobileNo}
              onChange={(e) => setEditUserData({ ...editUserData, mobileNo: e.target.value })}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setEditUserModalOpen(false)} disabled={updatingUser}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleUpdateUser} disabled={updatingUser}>
            {updatingUser ? (
              <>
                <Spinner size="sm" className="me-2" />
                Updating...
              </>
            ) : (
              <>
                <i className="ri-check-line me-1" />
                Update User
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      <CreateBookingModal
        isOpen={bookingModalOpen}
        onClose={() => {
          setBookingModalOpen(false);
          setSelectedUserForBooking(null);
        }}
        skipUserStep={true}
        preselectedUser={selectedUserForBooking}
        onBookingCreated={async () => {
          setBookingModalOpen(false);
          setSelectedUserForBooking(null);
          await dispatch(fetchUsers(filterParams));
        }}
      />

      {/* User Details Modal */}
      <Modal
        isOpen={detailsModalOpen}
        toggle={() => setDetailsModalOpen(false)}
        size="xl"
        centered
        className="full-height-model"
      >
        <ModalHeader toggle={() => setDetailsModalOpen(false)}>
          User Details - {selectedUser?.name || "N/A"}
        </ModalHeader>
        <ModalBody>
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : userDetails ? (
            <div>
              {/* User Information */}
              <Card className="mb-4">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-start mb-4">
                    <div>
                      <h4 className="card-title mb-1">{userDetails.name || "N/A"}</h4>
                      <p className="text-muted mb-2">User ID: {userDetails.userId}</p>
                    </div>
                    <Badge
                      color={userDetails.loginType === 'phone' ? 'primary' : userDetails.loginType === 'admin' ? 'danger' : 'info'}
                      className="text-uppercase fs-12"
                      pill
                    >
                      {userDetails.loginType || "N/A"}
                    </Badge>
                  </div>

                  <Row>
                    <Col md={6}>
                      <div className="mb-3">
                        <label className="form-label text-muted">Phone Number</label>
                        <p className="mb-0 fs-14">{userDetails.mobileNo || "N/A"}</p>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <label className="form-label text-muted">Email</label>
                        <p className="mb-0 fs-14">{userDetails.email || "N/A"}</p>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <label className="form-label text-muted">Created At</label>
                        <p className="mb-0 fs-14">
                          {userDetails.createdAt ? new Date(userDetails.createdAt).toLocaleString() : "N/A"}
                        </p>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-3">
                        <label className="form-label text-muted">Last Updated</label>
                        <p className="mb-0 fs-14">
                          {userDetails.updatedAt ? new Date(userDetails.updatedAt).toLocaleString() : "N/A"}
                        </p>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              {/* Stats Grid */}
              <Row className="mb-4">
                <Col xl={3} md={6}>
                  <Card className="card-animate">
                    <CardBody>
                      <div className="d-flex align-items-center">
                        <div className="flex-grow-1">
                          <p className="text-uppercase fw-medium text-muted mb-0">Total Bookings</p>
                          <h4 className="fs-22 fw-semibold mb-0">
                            {userDetails.totalFarmBookings || 0}
                          </h4>
                        </div>
                        <div className="flex-shrink-0">
                          <div className="avatar-sm flex-shrink-0">
                            <span className="avatar-title bg-primary-subtle text-primary rounded fs-3">
                              <i className="ri-calendar-check-line"></i>
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                <Col xl={3} md={6}>
                  <Card className="card-animate">
                    <CardBody>
                      <div className="d-flex align-items-center">
                        <div className="flex-grow-1">
                          <p className="text-uppercase fw-medium text-muted mb-0">Total Payment</p>
                          <h4 className="fs-22 fw-semibold mb-0">
                            ₹{userDetails.totalPaymentAmount || 0}
                          </h4>
                        </div>
                        <div className="flex-shrink-0">
                          <div className="avatar-sm flex-shrink-0">
                            <span className="avatar-title bg-success-subtle text-success rounded fs-3">
                              <i className="ri-money-dollar-circle-line"></i>
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                <Col xl={3} md={6}>
                  <Card className="card-animate">
                    <CardBody>
                      <div className="d-flex align-items-center">
                        <div className="flex-grow-1">
                          <p className="text-uppercase fw-medium text-muted mb-0">Payment Received</p>
                          <h4 className="fs-22 fw-semibold mb-0">
                            ₹{userDetails.totalPaymentReceived || 0}
                          </h4>
                        </div>
                        <div className="flex-shrink-0">
                          <div className="avatar-sm flex-shrink-0">
                            <span className="avatar-title bg-info-subtle text-info rounded fs-3">
                              <i className="ri-checkbox-circle-line"></i>
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                <Col xl={3} md={6}>
                  <Card className="card-animate">
                    <CardBody>
                      <div className="d-flex align-items-center">
                        <div className="flex-grow-1">
                          <p className="text-uppercase fw-medium text-muted mb-0">Payment Pending</p>
                          <h4 className="fs-22 fw-semibold mb-0">
                            ₹{userDetails.totalPaymentPending || 0}
                          </h4>
                        </div>
                        <div className="flex-shrink-0">
                          <div className="avatar-sm flex-shrink-0">
                            <span className="avatar-title bg-warning-subtle text-warning rounded fs-3">
                              <i className="ri-time-line"></i>
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* Bookings Table */}
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="card-title mb-0">Booking History</h5>
                  </div>

                  {userDetails.bookings && userDetails.bookings.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-striped table-bordered">
                        <thead>
                          <tr>
                            <th>Farm Name</th>
                            <th>Location</th>
                            <th>Start Date</th>
                            <th>End Date</th>
                            <th>Hours</th>
                            <th>Price</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {userDetails.bookings.map((booking, index) => {
                            const duration = getBookingDuration(booking.bookingType);
                            const badgeColor = getBookingTypeBadge(booking.bookingType);

                            return (
                              <tr key={index}>
                                <td>{booking.farmName}</td>
                                <td>{booking.farmLocation}</td>
                                <td>
                                  {booking.bookingDate
                                    ? new Date(booking.bookingDate).toLocaleDateString('en-US', {
                                      weekday: 'short',
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric'
                                    })
                                    : "N/A"}
                                </td>
                                <td>
                                  {booking.bookingEndDate
                                    ? new Date(booking.bookingEndDate).toLocaleDateString('en-US', {
                                      weekday: 'short',
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric'
                                    })
                                    : "N/A"}
                                </td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <i className="ri-time-line me-2"></i>
                                    <span className="fw-semibold">{duration.hours}H</span>
                                  </div>
                                </td>
                                <td>
                                  <div className="d-flex flex-column">
                                    {booking.discountAmount > 0 && (
                                      <span className="text-muted text-decoration-line-through fs-11">
                                        ₹{booking.originalPrice}
                                      </span>
                                    )}
                                    <span className="fw-bold text-success">
                                      ₹{booking.finalPrice}
                                    </span>
                                    {booking.discountAmount > 0 && (
                                      <span className="text-danger fs-11">
                                        -₹{booking.discountAmount}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <Badge
                                    color={booking.paymentStatus === 'paid' ? 'success' :
                                      booking.paymentStatus === 'partial' ? 'info' :
                                        booking.paymentStatus === 'incomplete' ? 'warning' : 'danger'}
                                    className="text-uppercase"
                                  >
                                    {booking.paymentStatus === 'paid' ? 'Paid' :
                                      booking.paymentStatus === 'partial' ? 'Outstanding' :
                                        booking.paymentStatus === 'incomplete' ? 'Pending' : 'Cancelled'}
                                  </Badge>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <i className="ri-calendar-line text-muted fs-48"></i>
                      <h5 className="mt-3">No bookings found</h5>
                      <p className="text-muted">This user hasn't made any bookings yet.</p>
                    </div>
                  )}
                </CardBody>
              </Card>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-muted">Failed to load user details.</p>
            </div>
          )}
        </ModalBody>
      </Modal>

      {/* Single Delete Dialog */}
      <DeleteDialog
        isOpen={deleteModalOpen}
        title="Delete User"
        message="Are you sure you want to delete this user? This action cannot be undone."
        confirmLabel="Delete User"
        cancelLabel="Cancel"
        confirmColor="danger"
        onConfirm={handleSingleDeleteConfirm}
        onCancel={() => setDeleteModalOpen(false)}
        loading={deleting}
      />

      {/* Bulk Delete Dialog */}
      <DeleteDialog
        isOpen={bulkDeleteModalOpen}
        title="Delete Multiple Users"
        message={`Are you sure you want to delete ${deleteUserIds.length} selected user(s)? This action cannot be undone.`}
        confirmLabel={`Delete ${deleteUserIds.length} User${deleteUserIds.length !== 1 ? 's' : ''}`}
        cancelLabel="Cancel"
        confirmColor="danger"
        onConfirm={handleBulkDeleteConfirm}
        onCancel={() => setBulkDeleteModalOpen(false)}
        loading={deleting}
      />
    </React.Fragment>
  );
};

export default Users;